/**
 * @module app.cd
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/api/api';
import { guid } from 'app/shared/lib/guid';
import { bindable } from 'aurelia-typed-observable-plugin';
import { IProviderSearchFilters, ProviderService } from 'app/cd/services/provider-service';
import { LookupService } from 'app/core/services/lookup-service';
import { IDisplayOptions } from 'app/shared/models/essette-interfaces';
import { EssAutocompleteCustomElement } from 'app/shared/elements/forms/ess-autocomplete';
import { AuthService } from 'app/um/services/auth-service';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import * as Core from 'app/core/models/essette-api';
import * as CD from 'app/cd/models/essette-api';

export interface IProviderSearchOptions {
    isContracted: IDisplayOptions;
}

@autoinject()
export class ProviderSearchFilters extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.object({ defaultBindingMode: bindingMode.twoWay, changeHandler: 'setFilters' })
    public searchFilters: IProviderSearchFilters = {};

    @bindable.objectNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'setOptions' })
    public options: IProviderSearchOptions | null = null;

    @bindable.arrayNull()
    public availableFilters: string[] | null = [
        'identifier', 'npi', 'providerFirstName', 'providerLastName', 'specialty', 'gender',
        'facilityName', 'groupName', 'street1', 'street2', 'city', 'region', 'postalCode', 'tax'
    ];

    @bindable.stringNull()
    protected labelId: string | null = null;

    @bindable.array()
    protected regions: Core.AddressRegion[] = [];
    @bindable.array()
    protected genders: Core.Gender[] = [];

    public specialtySearchQuery: string | null = null;
    protected specialties: CD.ProviderSpecialty[] = [];
    protected types: CD.AuthProviderType[] = [];

    private readonly defaultOptions: IProviderSearchOptions = {
        isContracted: { isVisible: false, isLocked: false, defaultValue: false }
    }

    protected maxProviderIdentifierLength: number = 50;
    protected maxNPILength: number = 10;
    protected maxFirstNameLength: number = 50;
    protected maxLastNameLength: number = 50;
    protected maxSpecialtyLength: number = 155; // Take account of both Specialty Description and Code Length
    protected maxGroupLength: number = 50;
    protected maxCityLength: number = 30;
    protected maxPostalCodeLength: number = 10;
    protected maxTaxLength: number = 50;

    // [TODO] BH: Uncap these two lengths (back to 100) when we're able to send query parameters in a body
    protected maxFacilityLength: number = 75; // true limit is 100, but too many maxed-out filters upsets the API
    protected maxStreet1Length: number = 75; // true limit is 100, but too many maxed-out filters upsets the API
    protected maxStreet2Length: number = 75; // true limit is 100, but too many maxed-out filters upsets the API

    protected id: guid = guid();

    ////****************** Element References ***************************************////
    protected $specialtiesAutoComplete!: EssAutocompleteCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly lookupService: LookupService,
        private readonly providerService: ProviderService,
        private readonly authService: AuthService,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(): void {
        this.loadLookups();
    }

    ////****************** Events ***************************************************////


    ////****************** Functions ************************************************////
    protected setFilters(filters: IProviderSearchFilters): void {
        if(!filters.specialty && this.$specialtiesAutoComplete) this.$specialtiesAutoComplete.clear();
    }

    protected setOptions(options: IProviderSearchOptions): void {
        this.options = Object.assign<IProviderSearchOptions, Partial<IProviderSearchOptions>>(this.defaultOptions, options || {});
    }

    public clearSpecialty(): void {
        this.specialtySearchQuery = null;
        this.specialties.splice(0);
        this.searchFilters.specialty = undefined;
    }

    protected filterVisible(name: string): boolean {
        if(!this.availableFilters) return true;

        const index = this.availableFilters.indexOf(name);

        return index >= 0;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async searchSpecialties(searchValue: string): Promise<void> {
        try {
            this.state.beginLoad();
            const fields = ['code', 'description'];
            const response = await this.providerService.searchSpecialties(searchValue, undefined, undefined, fields);
            this.specialties = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadLookups(): Promise<void> {
        const sorting = new Sorting();
        sorting.add({
            reference: 'description',
            direction: SortDirection.Ascending
        });

        const options: Partial<IQueryLoadOptions> = {
            all: true,
            sorting
        };

        const loadRegions = this.filterVisible('region') && !this.regions.length;
        const loadGenders = this.filterVisible('gender') && !this.genders.length;
        const loadTypes = this.filterVisible('type') && !this.types.length;
        const statesFields = ['code', 'description'];
        const fields = ['description'];

        try {
            this.state.beginLoad();

            const [regions, genders, types] = await Promise.all([
                loadRegions ? this.lookupService.loadStates(options, undefined, statesFields) : null,
                loadGenders ? this.lookupService.loadGenders(options, undefined, undefined, true, fields) : null,
                loadTypes ? this.authService.loadAuthProviderTypes(options, undefined, fields) : null
            ]);

            if(regions) this.regions = regions.results;
            if(genders) this.genders = genders.results;
            if(types) this.types = types.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
